import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Box, LoadingOverlay, Text, Stack } from "@mantine/core";
import { notifyError, notifySuccess } from "../../utils/notify";
import moment from "moment";
import {
  DateRangePreset,
  ReportDefinition,
  ReportRunResult,
  SalonReport,
  SortRule,
} from "../../types/reports";
import { useReports } from "../../hooks/useReports";
import ReportTable from "./ReportTable";
import ReportToolbar from "./ReportToolbar";
import {
  exportReportToCSV,
  exportReportToExcel,
} from "../../utils/reportUtils";

interface Props {
  salonId: string;
  definition: ReportDefinition;
  report: SalonReport;
  isDeleting?: boolean;
  onReportUpdated: (report: SalonReport) => void;
  onDelete: () => void;
}

/** Build the per-filter map from a salon_report row. */
function filterValuesFromReport(
  report: SalonReport,
  definition: ReportDefinition,
): Record<string, string[]> {
  return Object.fromEntries(
    definition.filters.map((f) => [
      f.key,
      ((report.filter_config?.[f.key] as string[]) || []).slice(),
    ]),
  );
}

function sameStringSet(a: string[], b: string[]): boolean {
  if (a.length !== b.length) return false;
  for (const v of a) if (!b.includes(v)) return false;
  return true;
}

export default function ReportView({
  salonId,
  definition,
  report,
  isDeleting = false,
  onReportUpdated,
  onDelete,
}: Props) {
  const { runSalonReport, updateSalonReport } = useReports();

  // ---- Effective preset / date range ------------------------------------
  // Custom dates are kept as "YYYY-MM-DD" strings throughout — converting
  // to a JS Date forces a timezone interpretation (UTC midnight), which
  // shifts the day backward in any timezone west of UTC when re-formatted.
  const initialPreset: DateRangePreset =
    report.default_date_range || definition.default_date_range;
  const [preset, setPreset] = useState<DateRangePreset>(initialPreset);
  const [customStart, setCustomStart] = useState<string | null>(
    report.fixed_start_dt,
  );
  const [customEnd, setCustomEnd] = useState<string | null>(
    report.fixed_end_dt,
  );

  const handleDateRangeChange = useCallback(
    (
      nextPreset: DateRangePreset,
      nextStart: string | null,
      nextEnd: string | null,
    ) => {
      setPreset(nextPreset);
      setCustomStart(nextStart);
      setCustomEnd(nextEnd);
    },
    [],
  );

  // ---- Run state ---------------------------------------------------------
  const [result, setResult] = useState<ReportRunResult | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hiddenColumns, setHiddenColumns] = useState<string[]>(
    report.hidden_columns || [],
  );
  // Opt-in visibility for columns the definition marks default_hidden
  // (e.g. the Transaction Log payment-method breakdown columns). Kept
  // separate from hiddenColumns: a default_hidden column is shown only
  // when its key is in here.
  const [shownColumns, setShownColumns] = useState<string[]>(
    report.shown_columns || [],
  );
  const [sortField, setSortField] = useState<string | null>(
    report.sort_config?.[0]?.column ?? null,
  );
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">(
    report.sort_config?.[0]?.direction ?? "asc",
  );
  const [filterValues, setFilterValues] = useState<Record<string, string[]>>(
    () => filterValuesFromReport(report, definition),
  );
  // Debounced mirror of filterValues — fetchData reads this so rapid
  // TagsInput edits coalesce into a single API call instead of one per
  // keystroke. Live filtering stays intentional, just throttled.
  const [debouncedFilterValues, setDebouncedFilterValues] = useState(() =>
    filterValuesFromReport(report, definition),
  );
  useEffect(() => {
    const handle = setTimeout(() => {
      setDebouncedFilterValues(filterValues);
    }, 300);
    return () => clearTimeout(handle);
  }, [filterValues]);

  const [isSaving, setIsSaving] = useState(false);

  // Bumped after Save View so the report re-runs even if no fetch dep
  // changed (e.g. only filters or hidden columns moved).
  const [runEpoch, setRunEpoch] = useState(0);

  // Skip the run while the user is mid-pick on a custom range. The
  // toolbar lets them open the popover, switch to "Custom range", then
  // pick start and end — until both are chosen there's nothing valid
  // to send the backend.
  const customRangeReady =
    preset !== "fixed_dates" || (customStart !== null && customEnd !== null);

  // The static definition (from list_report_definitions) declares only
  // the report's fixed columns. Runners that surface dynamic per-run
  // columns (e.g. Daily Summary's per-salon Off-Platform Payment method
  // columns, and the Transaction Log payment-method breakdown) return
  // them in result.definition. Prefer that augmented version anywhere we
  // iterate columns to render headers, build the totals row, export, or
  // populate the visibility menu. Falls back to the prop on the initial
  // render before runSalonReport completes.
  const effectiveDefinition: ReportDefinition =
    result?.definition ?? definition;

  // Resolve which columns are hidden right now. A default_hidden column
  // is hidden unless the user opted it in (shownColumns); every other
  // column is hidden when the user opted it out (hiddenColumns). We hand
  // this single derived list to the toolbar, table, and exporters so they
  // stay unaware of the default_hidden / shown_columns split.
  const effectiveHiddenColumns = useMemo<string[]>(() => {
    const shown = new Set(shownColumns);
    const hidden = new Set(hiddenColumns);
    return effectiveDefinition.columns
      .filter((c) => (c.default_hidden ? !shown.has(c.key) : hidden.has(c.key)))
      .map((c) => c.key);
  }, [effectiveDefinition, shownColumns, hiddenColumns]);

  const fetchData = useCallback(async () => {
    if (!customRangeReady) {
      return;
    }
    try {
      setIsLoading(true);
      setError(null);
      // Send every filter, empty ones included: the backend merges these
      // over the saved filter_config, so omitting a cleared filter would
      // let its saved value keep applying until the view is saved. An
      // empty list means "no filter" (or the default, for enum_select).
      const filterOverrides: Record<string, unknown> = {
        ...debouncedFilterValues,
      };
      const payload = await runSalonReport(salonId, report.id, {
        date_range_preset: preset,
        start_date: preset === "fixed_dates" ? customStart : null,
        end_date: preset === "fixed_dates" ? customEnd : null,
        filter_overrides: filterOverrides,
      });
      setResult(payload);
    } catch (e) {
      console.error("Failed to run report:", e);
      setError("Failed to run report. Please try again.");
    } finally {
      setIsLoading(false);
    }
  }, [
    runSalonReport,
    salonId,
    report.id,
    preset,
    customStart,
    customEnd,
    customRangeReady,
    debouncedFilterValues,
  ]);

  // Re-run whenever fetchData identity changes OR runEpoch ticks.
  useEffect(() => {
    fetchData();
  }, [fetchData, runEpoch]);

  const sortedRows = useMemo(() => {
    if (!result) return [];
    if (!sortField) return result.rows;
    const sorted = [...result.rows];
    sorted.sort((a, b) => {
      const av = a[sortField];
      const bv = b[sortField];
      if (av === bv) return 0;
      if (av == null) return 1;
      if (bv == null) return -1;
      if (typeof av === "number" && typeof bv === "number") {
        return sortDirection === "asc" ? av - bv : bv - av;
      }
      const as = String(av);
      const bs = String(bv);
      return sortDirection === "asc"
        ? as.localeCompare(bs)
        : bs.localeCompare(as);
    });
    return sorted;
  }, [result, sortField, sortDirection]);

  // ---- Dirty detection ---------------------------------------------------
  // True when on-screen state diverges from the saved salon_reports row.
  // Drives the Save View / Reset affordances in the toolbar.
  const isDirty = useMemo(() => {
    const savedPreset =
      report.default_date_range || definition.default_date_range;
    if (preset !== savedPreset) return true;

    if (preset === "fixed_dates") {
      if (customStart !== report.fixed_start_dt) return true;
      if (customEnd !== report.fixed_end_dt) return true;
    }

    if (!sameStringSet(report.hidden_columns || [], hiddenColumns)) {
      return true;
    }

    if (!sameStringSet(report.shown_columns || [], shownColumns)) {
      return true;
    }

    const savedSort = report.sort_config?.[0];
    if ((savedSort?.column ?? null) !== sortField) return true;
    if (sortField && (savedSort?.direction ?? "asc") !== sortDirection) {
      return true;
    }

    for (const f of definition.filters) {
      const saved = (report.filter_config?.[f.key] as string[]) || [];
      const current = filterValues[f.key] || [];
      if (!sameStringSet(saved, current)) return true;
    }

    return false;
  }, [
    report,
    definition,
    preset,
    customStart,
    customEnd,
    hiddenColumns,
    shownColumns,
    sortField,
    sortDirection,
    filterValues,
  ]);

  const handleToggleColumn = (key: string) => {
    const col = effectiveDefinition.columns.find((c) => c.key === key);
    // default_hidden columns track their visibility in shownColumns
    // (presence = visible); normal columns track it in hiddenColumns
    // (presence = hidden). Toggling flips membership in the right list.
    if (col?.default_hidden) {
      setShownColumns((prev) =>
        prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key],
      );
    } else {
      setHiddenColumns((prev) =>
        prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key],
      );
    }
  };

  const handleFilterChange = (key: string, value: string[]) => {
    setFilterValues((prev) => ({ ...prev, [key]: value }));
  };

  const handleSort = (column: string) => {
    if (sortField === column) {
      setSortDirection((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortField(column);
      setSortDirection("asc");
    }
  };

  const handleDownloadCsv = () => {
    if (!result) return;
    exportReportToCSV(effectiveDefinition, sortedRows, {
      hiddenColumns: effectiveHiddenColumns,
      customName: report.custom_name ?? undefined,
    });
  };

  const handleDownloadExcel = () => {
    if (!result) return;
    exportReportToExcel(effectiveDefinition, sortedRows, {
      hiddenColumns: effectiveHiddenColumns,
      customName: report.custom_name ?? undefined,
    });
  };

  // Persist the on-screen view as the new defaults on this salon_report.
  // Mirrors the deprecated "Customize" drawer's save path.
  const handleSaveView = async () => {
    try {
      setIsSaving(true);
      const sortConfig: SortRule[] = sortField
        ? [{ column: sortField, direction: sortDirection }]
        : [];
      const filterConfig: Record<string, unknown> = {};
      for (const [key, value] of Object.entries(filterValues)) {
        if (value.length > 0) filterConfig[key] = value;
      }
      const { report: updated } = await updateSalonReport(salonId, report.id, {
        default_date_range: preset,
        fixed_start_dt: preset === "fixed_dates" ? customStart : null,
        fixed_end_dt: preset === "fixed_dates" ? customEnd : null,
        hidden_columns: hiddenColumns,
        shown_columns: shownColumns,
        sort_config: sortConfig,
        filter_config: filterConfig,
      });
      onReportUpdated(updated);
      // Re-sync local state so isDirty drops back to false.
      setHiddenColumns(updated.hidden_columns || []);
      setShownColumns(updated.shown_columns || []);
      setSortField(updated.sort_config?.[0]?.column ?? null);
      setSortDirection(updated.sort_config?.[0]?.direction ?? "asc");
      setCustomStart(updated.fixed_start_dt);
      setCustomEnd(updated.fixed_end_dt);
      setPreset(
        (updated.default_date_range ||
          definition.default_date_range) as DateRangePreset,
      );
      setFilterValues(filterValuesFromReport(updated, definition));
      // Re-run with whatever the server stored.
      setRunEpoch((n) => n + 1);
      notifySuccess("Saved as the default for this report.", {
        title: "View saved",
      });
    } catch (e) {
      console.error("Failed to save view:", e);
      notifyError("Try again in a moment.", { title: "Couldn't save view" });
    } finally {
      setIsSaving(false);
    }
  };

  // Discard local edits and snap back to the persisted defaults. The
  // useEffect-driven re-fetch picks up automatically when any fetch dep
  // (preset / dates / filters) actually changes; we also bump runEpoch
  // so the unusual case where everything was already equal still
  // reissues a clean run.
  const handleReset = () => {
    setPreset(
      (report.default_date_range ||
        definition.default_date_range) as DateRangePreset,
    );
    setCustomStart(report.fixed_start_dt);
    setCustomEnd(report.fixed_end_dt);
    setHiddenColumns(report.hidden_columns || []);
    setShownColumns(report.shown_columns || []);
    setSortField(report.sort_config?.[0]?.column ?? null);
    setSortDirection(report.sort_config?.[0]?.direction ?? "asc");
    setFilterValues(filterValuesFromReport(report, definition));
    setRunEpoch((n) => n + 1);
  };

  return (
    <Stack gap="md">
      <ReportToolbar
        definition={effectiveDefinition}
        preset={preset}
        customStart={customStart}
        customEnd={customEnd}
        onDateRangeChange={handleDateRangeChange}
        hiddenColumns={effectiveHiddenColumns}
        onToggleColumn={handleToggleColumn}
        filterValues={filterValues}
        onFilterChange={handleFilterChange}
        isDirty={isDirty}
        isSaving={isSaving}
        isLoading={isLoading}
        isDeleting={isDeleting}
        onSaveView={handleSaveView}
        onReset={handleReset}
        onDownloadCsv={handleDownloadCsv}
        onDownloadExcel={handleDownloadExcel}
        onDelete={onDelete}
      />
      {error && (
        <Text c="red" size="sm">
          {error}
        </Text>
      )}
      {result && (
        <Text c="dimmed" size="sm">
          {sortedRows.length} row{sortedRows.length === 1 ? "" : "s"}
          {" · "}
          {moment(result.date_range.start).format("MMM D, YYYY")} –{" "}
          {moment(result.date_range.end).format("MMM D, YYYY")}
        </Text>
      )}
      {/* Scope the loading spinner to the table so the toolbar above
          remains visible (and disabled) while the report re-runs. */}
      <Box pos="relative" mih={200}>
        <LoadingOverlay
          visible={isLoading}
          zIndex={2}
          overlayProps={{ blur: 1 }}
        />
        <ReportTable
          definition={effectiveDefinition}
          rows={sortedRows}
          hiddenColumns={effectiveHiddenColumns}
          sortField={sortField}
          sortDirection={sortDirection}
          onSort={handleSort}
        />
      </Box>
    </Stack>
  );
}
